// src/modules/Rules/components/modals/ConfirmRecommendedRules.tsx
import { forwardRef, useImperativeHandle, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@shared/components/ui/button';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { EnterIcon, EscIcon } from '@shared/components/ui/icons';

export interface ConfirmRecommendedRulesRef {
  /**
   * Abre la modal de confirmación para aplicar reglas recomendadas.
   * @param onConfirm Callback que se ejecutará si el usuario acepta.
   */
  open: (onConfirm: () => void) => void;
}

export const ConfirmRecommendedRules = forwardRef<ConfirmRecommendedRulesRef>((_, ref) => {
  const { t } = useTranslation('rulesModule');
  const modalRef = useRef<ModalCenterRef>(null);
  const confirmRef = useRef<() => void>(() => {});

  useImperativeHandle(ref, () => ({
    open: (onConfirm: () => void) => {
      confirmRef.current = onConfirm;
      modalRef.current?.openModal();
    },
  }));

  const handleConfirm = () => {
    confirmRef.current();
    confirmRef.current = () => {};
    modalRef.current?.closeModal();
  };

  return (
    <ModalCenter ref={modalRef} title={t('modal.recommendedRules.title')} type="default" width="default">
      <div className="space-y-4">
        <p className="text-sm">{t('modal.recommendedRules.message')}</p>

        <div className="flex justify-end gap-2 pt-4">
          <Button variant="outline" size="lg" onClick={() => modalRef.current?.closeModal()}>
            {t('modal.buttons.cancel')}
            <EscIcon className="w-4 h-4" />
          </Button>
          <Button variant="primary" size="lg" onClick={handleConfirm}>
            {t('modal.buttons.confirm')}
            <EnterIcon className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </ModalCenter>
  );
});

ConfirmRecommendedRules.displayName = 'ConfirmRecommendedRules';
